/**
 * The `@` mentions in a skill's instructions. They are stored as plain
 * markdown links, so SKILL.md stays readable:
 *
 *   [Cover Letter Template](/firm-knowledge?entry=<id>)          a library document
 *   [Draft the RFE Response](/firm-knowledge?tab=skills&skill=<id>)  another skill
 *
 * and shown in the editor as `@Label` chips. The editor works on the display
 * text plus where the chips are (`InstructionModel`); `parseInstructions` and
 * `toInstructions` convert between the two. No React here.
 */
import {
  activeMentionAt,
  type ActiveMention
} from "@/features/caseIde/chat/mentions";

export { activeMentionAt, type ActiveMention };

export type InstructionTarget = {
  kind: "entry" | "skill";
  id: string;
  label: string;
};

/** A chip in the display text: the run `@Label` at [start, end). */
export type InstructionRange = {
  start: number;
  end: number;
  target: InstructionTarget;
};

export type InstructionModel = {
  display: string;
  mentions: InstructionRange[];
};

export const EMPTY_INSTRUCTIONS: InstructionModel = {
  display: "",
  mentions: []
};

const LINK = /\[([^\]\n]+)\]\((\/firm-knowledge\?[^)\s]+)\)/g;

/** Where a mention's link points. */
export const mentionHref = (t: Pick<InstructionTarget, "kind" | "id">) =>
  t.kind === "entry"
    ? `/firm-knowledge?entry=${encodeURIComponent(t.id)}`
    : `/firm-knowledge?tab=skills&skill=${encodeURIComponent(t.id)}`;

/** The target a Firm Knowledge link names, if it names one. */
const targetOfHref = (
  href: string,
  label: string
): InstructionTarget | null => {
  const params = new URLSearchParams(href.slice(href.indexOf("?") + 1));
  const entry = params.get("entry");
  if (entry) return { kind: "entry", id: entry, label };
  const skill = params.get("skill");
  if (skill && params.get("tab") === "skills")
    return { kind: "skill", id: skill, label };
  return null;
};

/** A label that can sit inside `[…]` and in one display line. */
const cleanLabel = (label: string) =>
  label
    .replace(/[[\]\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "Untitled";

const byStart = (a: InstructionRange, b: InstructionRange) => a.start - b.start;

/** Stored instructions to display text with chips. */
export const parseInstructions = (text: string): InstructionModel => {
  let display = "";
  let last = 0;
  const mentions: InstructionRange[] = [];
  for (const m of text.matchAll(LINK)) {
    const at = m.index ?? 0;
    const target = targetOfHref(m[2], cleanLabel(m[1]));
    if (target) {
      display += text.slice(last, at);
      const chip = `@${target.label}`;
      mentions.push({
        start: display.length,
        end: display.length + chip.length,
        target
      });
      display += chip;
      last = at + m[0].length;
    }
  }
  return { display: display + text.slice(last), mentions };
};

/** Display text with chips back to the stored markdown. */
export const toInstructions = (model: InstructionModel): string => {
  let out = "";
  let last = 0;
  for (const m of [...model.mentions].sort(byStart)) {
    out += model.display.slice(last, m.start);
    out += `[${cleanLabel(m.target.label)}](${mentionHref(m.target)})`;
    last = m.end;
  }
  return out + model.display.slice(last);
};

/** The display text split into plain runs and chips, for the mirror layer. */
export type InstructionSegment =
  | { type: "text"; text: string }
  | { type: "mention"; text: string; target: InstructionTarget };

export const instructionSegments = (
  model: InstructionModel
): InstructionSegment[] => {
  const out: InstructionSegment[] = [];
  let last = 0;
  for (const m of [...model.mentions].sort(byStart)) {
    if (m.start > last)
      out.push({ type: "text", text: model.display.slice(last, m.start) });
    out.push({
      type: "mention",
      text: model.display.slice(m.start, m.end),
      target: m.target
    });
    last = m.end;
  }
  if (last < model.display.length)
    out.push({ type: "text", text: model.display.slice(last) });
  return out;
};

/** Which library documents and skills the text mentions, each once. */
export const mentionedIds = (
  model: InstructionModel
): { entries: string[]; skills: string[] } => {
  const entries = new Set<string>();
  const skills = new Set<string>();
  for (const m of model.mentions)
    (m.target.kind === "entry" ? entries : skills).add(m.target.id);
  return { entries: [...entries], skills: [...skills] };
};

/**
 * Replace display[start, end) with a chip and a trailing space. Chips the
 * span overlapped are dropped; later ones shift.
 */
export const insertMention = (
  model: InstructionModel,
  start: number,
  end: number,
  target: InstructionTarget
): { model: InstructionModel; caret: number } => {
  const chip = `@${cleanLabel(target.label)}`;
  const inserted = `${chip} `;
  const delta = inserted.length - (end - start);
  const mentions = model.mentions
    .filter((m) => m.end <= start || m.start >= end)
    .map((m) =>
      m.start >= end ? { ...m, start: m.start + delta, end: m.end + delta } : m
    );
  mentions.push({ start, end: start + chip.length, target });
  return {
    model: {
      display:
        model.display.slice(0, start) + inserted + model.display.slice(end),
      mentions: mentions.sort(byStart)
    },
    caret: start + inserted.length
  };
};

/**
 * Bring the chips in line with what the textarea now holds after a free-form
 * edit (the span between the common prefix and suffix). Chips before it stay,
 * chips after it shift. A chip the edit touches is deleted whole when the
 * edit only deleted (Backspace at its end), and turned back into plain text
 * when something was typed into it. `caret` is set when the display had to
 * change beyond what the textarea did.
 */
export const reconcileInstructions = (
  prev: InstructionModel,
  next: string
): { model: InstructionModel; caret?: number } => {
  if (prev.display === next) return { model: prev };
  if (!prev.mentions.length) return { model: { display: next, mentions: [] } };
  const a = prev.display;
  const max = Math.min(a.length, next.length);
  let p = 0;
  while (p < max && a[p] === next[p]) p += 1;
  let s = 0;
  while (s < max - p && a[a.length - 1 - s] === next[next.length - 1 - s])
    s += 1;
  const oldEnd = a.length - s;
  const inserted = next.slice(p, next.length - s);
  const deletion = inserted.length === 0;

  let cutStart = p;
  let cutEnd = oldEnd;
  const kept: InstructionRange[] = [];
  for (const m of prev.mentions) {
    if (m.end <= p || m.start >= oldEnd) kept.push(m);
    else if (deletion) {
      cutStart = Math.min(cutStart, m.start);
      cutEnd = Math.max(cutEnd, m.end);
    }
  }
  const display = a.slice(0, cutStart) + inserted + a.slice(cutEnd);
  const delta = display.length - a.length;
  const mentions = kept
    .filter((m) => m.end <= cutStart || m.start >= cutEnd)
    .map((m) =>
      m.start >= cutEnd
        ? { ...m, start: m.start + delta, end: m.end + delta }
        : m
    )
    .sort(byStart);
  return display === next
    ? { model: { display, mentions } }
    : { model: { display, mentions }, caret: cutStart + inserted.length };
};

/** What the `@` picker offers: library documents first, then skills. */
export type InstructionCandidate = InstructionTarget & { hint?: string };

/** Up to `perKind` of each kind, names starting with the query first. */
export const filterCandidates = (
  candidates: readonly InstructionCandidate[],
  query: string,
  perKind = 6
): InstructionCandidate[] => {
  const q = query.trim().toLowerCase();
  const rank = (c: InstructionCandidate) =>
    c.label.toLowerCase().startsWith(q) ? 0 : 1;
  const of = (kind: InstructionTarget["kind"]) =>
    candidates
      .filter(
        (c) => c.kind === kind && (!q || c.label.toLowerCase().includes(q))
      )
      .sort((a, b) => (q ? rank(a) - rank(b) : 0))
      .slice(0, perKind);
  return [...of("entry"), ...of("skill")];
};
